import * as fzy from "fzy.js";

/**
 * Get a score for how well the test string matches the source string. If the
 * test string cannot possibly match the source string, the score is
 * undefined.
 */
function fuzzyScore(source: string, test: string): number | undefined {
	if (!fzy.hasMatch(test, source)) {
		return undefined;
	}
	return fzy.score(test, source);
}

/**
 * A simple utility method to "fuzzy" match the string `source` against `test`.
 *
 * Wraps around the `fuzzyScore` method and handles multiple source
 * strings in an array, getting a score for each element and returning the
 * maximum.
 *
 * @param source - The string(s) to test against (ex: "Hello World"); if
 * 	an array is provided, each string in the array will be tested, and the
 * 	highest score will be returned.
 * @param test - The string to test with (ex: "ello")
 * @returns A score representing how close of a match there is
 */
export function multiScore(source: string | readonly string[], test: string): number | undefined {
	if (typeof source === "string") {
		return fuzzyScore(source, test);
	}

	let max: number | undefined = undefined;
	for (const s of source) {
		const score = fuzzyScore(s, test);
		if (score !== undefined && (max === undefined || max < score)) {
			max = score;
		}
	}
	return max;
}
